import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';

import { AppState } from '../../../../store';
import { ActionTypes } from '../../../../store/sngpc/types';

import { SNGPCCompany, SNGPCResponsible } from '../../../../interfaces/sngpc';

import Text from '../../../ui/Text';
import Select from '../../../ui/Form/Select';
import BaseForm from '../Form';

import * as Styles from './FormCompanyStyles';

type FormValues = {
  userType: string;
  pharmacy: string;
};

const validate = (values: FormValues): Partial<FormValues> => {
  const errors: Partial<FormValues> = {};

  if (!values.pharmacy) {
    errors.pharmacy = 'Selecione um tipo de usuário';
  }

  if (!values.userType) {
    errors.userType = 'Selecione uma farmácia';
  }

  return errors;
};

const FormCompany: React.FC = () => {
  const dispatch = useDispatch();

  const [companySelected, setCompanySelected] = useState<SNGPCCompany | null>(null);
  const [responsibleSelected, setResponsibleSelected] = useState<SNGPCResponsible | null>(null);

  const { currentStep, loading, error, empresas } = useSelector((state: AppState) => state.sngpc);

  const handleSelectedCompany = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const company = empresas?.filter((empresa) => event.target.value === empresa.cnpj);
    form.setFieldValue('pharmacy', event.target.value);

    if (company && company[0]) {
      setCompanySelected(company[0]);
    } else {
      setCompanySelected(null);
      setResponsibleSelected(null);
    }
  };

  const handleSelectedResponsible = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    form.setFieldValue('userType', event.target.value);
    const responsible = companySelected?.responsavel?.filter((item) => event.target.value === item.nome);

    if (responsible && responsible[0]) {
      setResponsibleSelected(responsible[0]);
    } else {
      setResponsibleSelected(null);
    }
  };

  const showError = (inputName: keyof FormValues): boolean =>
    Boolean(form.errors[inputName] && form.touched[inputName]);

  const form = useFormik<FormValues>({
    initialValues: {
      userType: '',
      pharmacy: '',
    },
    validate,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (companySelected) {
        dispatch({
          type: ActionTypes.HANDLE_SELECTED_COMPANY,
          payload: {
            empresaSelecionada: companySelected,
            responsavelSelecionado: responsibleSelected,
          },
        });
      }
    },
  });

  return (
    <Styles.Container>
      <Styles.Header>
        <Styles.Title>Empresa e Usuário</Styles.Title>
        <Text color="black80">Selecione a farmácia e tipo de usuário que deseja cadastrar.</Text>
      </Styles.Header>
      <BaseForm onSubmit={form.handleSubmit} loading={loading}error={error}  step={currentStep}>
        <Select
          onChange={handleSelectedCompany}
          label="Farmácia"
          placeHolder="Selecione a farmácia"
          name="pharmacy"
          errors={showError('pharmacy') ? form.errors.pharmacy : undefined}
        >
          <option value="">Selecione a farmácia</option>
          {empresas?.map((empresa) => (
            <option disabled={empresa.cadastrada} key={empresa.cnpj} value={empresa.cnpj}>
              {empresa.nomeFantasia} {empresa.cadastrada && ' (Já Cadastrada)'}
            </option>
          ))}
        </Select>

        <Select
          onChange={handleSelectedResponsible}
          disabled={companySelected === null}
          label="Tipo de usuário"
          placeHolder="Selecione o tipo de usuário"
          name="userType"
          errors={showError('userType') ? form.errors.userType : undefined}
        >
          <option value="">Selecione o tipo de usuário</option>
          {companySelected?.responsavel?.map((item) => (
            <option key={item.nome} value={item.nome}>
              {item.nome} ({item.tipo})
            </option>
          ))}
        </Select>
      </BaseForm>
    </Styles.Container>
  );
};

export default FormCompany;
